import { Link } from "react-router-dom";
import { Facebook, Instagram, Heart, Compass, Handshake, ChevronLeft, ChevronRight, Quote } from "lucide-react";
// Removed Navbar and Footer imports as they are handled by PublicLayout
import { Button } from "@/components/ui/button";
import NewsletterSignup from "@/components/ui/NewsletterSignup";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile"; // Import the hook

const testimonials = [
  {
    quote: "Att få läsa om andra i samma situation har hjälpt mig att inte känna mig ensam. Det är som att någon redan har gått vägen före mig – och vill hålla min hand.",
    role: "Anhörig dotter till mamma med Alzheimer"
  },
  {
    quote: "Jag trodde först inte att jag hade något att bidra med. Men när jag delade min berättelse fick jag så mycket kärlek tillbaka – och insåg hur mycket vi kan stötta varandra.",
    role: "Demensdrabbad i tidig fas"
  },
  {
    quote: "Att kunna ställa frågor till andra anhöriga, och få svar från någon som verkligen vet hur det är – det har varit ovärderligt. Inga filter, bara ärlighet och värme.",
    role: "Make till partner med Alzheimer"
  },
  {
    quote: "Som undersköterska i demensvård kan jag snabbt dela med mig av min kunskap till fler. Det känns fint att få hjälpa fler familjer.",
    role: "Vårdpersonal"
  },
  {
    quote: "Det här är den enda platsen där jag känner att jag kan vara både ledsen och hoppfull – utan att behöva förklara mig.",
    role: "Anhörig till yngre person med Alzheimer"
  },
  {
    quote: "Här får jag tips och råd som verkligen fungerar i vardagen. Finner tröst i denna grupp",
    role: "Anhörig fru till make med Frontallobsdemens"
  }
];

const Community = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useIsMobile(); // Use the hook
  const itemsPerPage = isMobile ? 1 : 3; // Determine items per page

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex >= testimonials.length - itemsPerPage ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - itemsPerPage : prevIndex - 1
    );
  };

  // Removed outer div and Navbar, handled by layout
  return (
    <>
      {/* Block 1: Hero Section */}
      <section className="flex-grow"> {/* Removed min-h-screen and flex-col */}
        {/* Header med bakgrundsbild */}
        <div className="relative h-[600px] mb-16">
          <div className="absolute inset-0">
            <img 
              src="/images/banner-community-02.webp" 
              alt="Community banner" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
          <div className="container mx-auto h-full flex items-center justify-center relative z-10 px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                En plats för värme, vägledning och gemenskap
              </h1>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <a
                  href="https://www.facebook.com/groups/demenshjaltar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-minnestig-purple text-white px-6 py-3 rounded-lg hover:bg-minnestig-darkpurple transition-colors text-lg font-medium" /* Updated classes */
                >
                  Gå med i Facebookgruppen
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/demenshjaltar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-minnestig-lavender text-minnestig-darkpurple px-6 py-3 rounded-lg hover:bg-minnestig-purple hover:text-white transition-colors text-lg font-medium" /* Updated classes */
                >
                  Följ oss på Instagram
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Nytt tvåkolumnsblock */}
        <div className="container mx-auto px-4 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Vänster kolumn */}
            <div className="prose prose-lg">
              <p className="text-gray-700 text-lg">
                I Demenshjältars community möts anhöriga, demensdrabbade och vårdpersonal. Här delar vi tankar, stöttar varandra – och påminner om att ingen behöver gå igenom detta ensam.
              </p>
            </div>
            
            {/* Höger kolumn */}
            <div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-4">
                Vad hittar du i gruppen?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-minnestig-purple mr-2">•</span>
                  Stöd från andra i samma situation
                </li>
                <li className="flex items-start">
                  <span className="text-minnestig-purple mr-2">•</span>
                  Möjlighet att ställa frågor och dela erfarenheter
                </li>
                <li className="flex items-start">
                  <span className="text-minnestig-purple mr-2">•</span>
                  Vägledning, pepp och kunskap
                </li>
                <li className="flex items-start">
                  <span className="text-minnestig-purple mr-2">•</span>
                  Ett tryggt utrymme
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Adminbild */}
        <section className="py-2 bg-white"> {/* Reduced vertical padding */}
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <img 
                src="/images/demenshjaltar-admin.webp" 
                alt="Demenshjältar administratörer" 
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* Block 2: Röster från Demenshjältar */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-12 text-center">
              Röster från Demenshjältar
            </h2>
            <div className="relative max-w-6xl mx-auto">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  // Adjust translateX based on itemsPerPage
                  style={{ transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)` }}
                >
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={index}
                      // Adjust width based on itemsPerPage (using Tailwind's responsive classes)
                      className={`flex-shrink-0 px-4 ${isMobile ? 'w-full' : 'w-1/3'}`}
                    >
                      <div className="bg-minnestig-beige p-6 rounded-xl h-full flex flex-col justify-between">
                        <div>
                          <div className="flex justify-end mb-4">
                            <Quote className="text-minnestig-purple/30" size={24} />
                          </div>
                          <p className="text-gray-700 italic">"{testimonial.quote}"</p>
                        </div>
                        <p className="text-minnestig-purple font-medium mt-6">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <button 
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white p-2 rounded-full shadow-lg hover:bg-minnestig-beige transition-colors"
                aria-label="Föregående"
              >
                <ChevronLeft className="w-6 h-6 text-minnestig-purple" />
              </button>
              
              <button 
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white p-2 rounded-full shadow-lg hover:bg-minnestig-beige transition-colors"
                aria-label="Nästa"
              >
                <ChevronRight className="w-6 h-6 text-minnestig-purple" />
              </button>

              {/* Adjust pagination dots based on itemsPerPage */}
              <div className="flex justify-center mt-8 gap-2">
                {Array.from({ length: testimonials.length - itemsPerPage + 1 }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === currentIndex ? 'bg-minnestig-purple' : 'bg-minnestig-beige'
                    }`}
                    aria-label={`Gå till citat ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </section>

      {/* Block 3: Det som gör Demenshjältar unikt */}
      <section className="py-16 bg-minnestig-beige">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-minnestig-teal mb-12 text-center">
            Det som gör Demenshjältar unikt
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-minnestig-lavender rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-minnestig-purple" />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">Empati på riktigt</h3>
              <p className="text-gray-600">Här får du vara både stark och sårbar – utan att dömas.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-minnestig-lavender rounded-full flex items-center justify-center mx-auto mb-4">
                <Compass className="w-6 h-6 text-minnestig-purple" />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">Vägledning steg för steg</h3>
              <p className="text-gray-600">Andra har gått före dig och kan dela vad som hjälpte just då.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <div className="w-12 h-12 bg-minnestig-lavender rounded-full flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-6 h-6 text-minnestig-purple" />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">Trygg gemenskap</h3>
              <p className="text-gray-600">Vi håller varandra – även när livet är som svårast.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Block 5: Bli en del av Demenshjältar */}
      <section className="py-16 bg-minnestig-beige">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-minnestig-teal mb-6">
            Vill du också känna gemenskapen?
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Gå med i vår Facebookgrupp där samtalet pågår varje dag.
            Varmt välkommen precis som du är.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://www.facebook.com/groups/demenshjaltar/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-minnestig-purple text-white px-6 py-3 rounded-lg hover:bg-minnestig-darkpurple transition-colors text-lg font-medium"
            >
              Gå med i Facebookgruppen
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/demenshjaltar"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-minnestig-lavender text-minnestig-darkpurple px-6 py-3 rounded-lg hover:bg-minnestig-purple hover:text-white transition-colors text-lg font-medium"
            >
              Följ oss på Instagram
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </section>
      
      <NewsletterSignup />
      {/* Removed Footer, handled by layout */}
    </>
  );
};

export default Community;
